import type { JSX } from "preact";
import { useMemo } from "preact/hooks";

interface IconProps {
  size?: number;
  className?: string;
}

export type PankAgentVariant = "default" | "green" | "pink";

interface PankAgentIconProps extends IconProps {
  variant?: PankAgentVariant;
}

// Color configurations for each variant
const variantColors = {
  default: {
    bgGradient: ["#3481C1", "#296699", "#3E8CCB"],
    strokeGradient: "#3973A3",
    iconFill: ["#EDF3F8", "#A3C2DB"],
    iconStroke: ["#EDF3F8", "#A3C2DB"],
  },
  green: {
    bgGradient: ["#34B8C1", "#299199", "#3EC2CB"],
    strokeGradient: "#35989F",
    iconFill: ["#EDF7F8", "#A3D8DB"],
    iconStroke: ["#EDF7F8", "#A3D8DB"],
  },
  pink: {
    bgGradient: ["#B045A2", "#C66EBA", "#CB3EB8"],
    strokeGradient: "#9F3591",
    iconFill: ["#F8EDF6", "#DBA3D4"],
    iconStroke: ["#F8EDF6", "#DBA3D4"],
  },
};

// Counter for unique IDs
let iconIdCounter = 0;

/**
 * PankAgent Logo Icon - Ghost lightning bolt
 * Supports color variants: default (blue), green, and pink
 */
export const PankAgentIcon = ({
  size = 24,
  className,
  variant = "default",
}: PankAgentIconProps): JSX.Element => {
  // Generate unique ID prefix for this instance
  const idPrefix = useMemo(() => `pankagent-${++iconIdCounter}`, []);

  const colors = variantColors[variant];

  return (
    <svg
      viewBox="0 0 620 620"
      width={size}
      height={size}
      class={className}
      fill="none"
    >
      <defs>
        <linearGradient
          id={`${idPrefix}-bg-gradient`}
          x1="161.345"
          y1="-780"
          x2="1056.46"
          y2="-488.163"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.174753" stop-color={colors.bgGradient[0]} />
          <stop offset="0.548077" stop-color={colors.bgGradient[1]} />
          <stop offset="0.810616" stop-color={colors.bgGradient[2]} />
        </linearGradient>
        <radialGradient
          id={`${idPrefix}-bg-radial`}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(310 310) scale(310)"
        >
          <stop offset="0.538462" stop-color="white" stop-opacity="0" />
          <stop offset="1" stop-color="white" />
        </radialGradient>
        <linearGradient
          id={`${idPrefix}-stroke-gradient`}
          x1="41.4439"
          y1="0"
          x2="741.283"
          y2="397.98"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="white" />
          <stop offset="0.514423" stop-color={colors.strokeGradient} />
          <stop offset="1" stop-color="white" />
        </linearGradient>
        <radialGradient
          id={`${idPrefix}-icon-fill`}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(-10.3333 251.444 -188.316 -7.66731 309.999 234.221)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.48348" stop-color={colors.iconFill[0]} />
          <stop offset="1" stop-color={colors.iconFill[1]} />
        </radialGradient>
        <radialGradient
          id={`${idPrefix}-icon-stroke`}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(-18.1073 505.415 -326.414 -11.484 311.502 80.5829)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.254808" stop-color={colors.iconStroke[0]} />
          <stop offset="1" stop-color={colors.iconStroke[1]} />
        </radialGradient>
        <filter
          id={`${idPrefix}-shadow`}
          x="137.777"
          y="75.7769"
          width="347.891"
          height="491.35"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="6.88889" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.27 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
      </defs>
      <circle
        cx="310"
        cy="310"
        r="308.278"
        fill={`url(#${idPrefix}-bg-gradient)`}
      />
      <circle
        cx="310"
        cy="310"
        r="308.278"
        fill={`url(#${idPrefix}-bg-radial)`}
        fill-opacity="0.2"
      />
      <circle
        cx="310"
        cy="310"
        r="308.278"
        stroke={`url(#${idPrefix}-stroke-gradient)`}
        stroke-width="3.44444"
      />
      <g filter={`url(#${idPrefix}-shadow)`}>
        <path
          d="M356.373 275.4L442.274 257.923C452.989 255.743 463.872 260.817 469.004 270.385C474.137 279.952 472.292 291.728 464.474 299.304L205.781 550.021C196.946 558.583 182.632 549.233 186.922 537.702L257.096 349.112L181.076 364.664C172.34 366.451 163.312 363.403 157.494 356.7C151.676 350.002 149.985 340.7 153.077 332.41L238.038 104.58C241.408 95.5451 250.035 89.5547 259.677 89.5547H405.954C416.055 89.5547 423.032 99.6633 419.45 109.108L356.373 275.4ZM273.704 144.178C262.295 140.101 247.041 153.289 239.634 173.634C232.227 193.979 235.471 213.777 246.88 217.853C258.29 221.93 273.544 208.741 280.951 188.397C288.358 168.052 285.114 148.254 273.704 144.178ZM342.097 144.178C330.688 140.101 315.434 153.289 308.027 173.634C300.62 193.979 303.864 213.777 315.273 217.853C326.683 221.93 341.937 208.741 349.344 188.397C356.751 168.052 353.507 148.254 342.097 144.178Z"
          fill={`url(#${idPrefix}-icon-fill)`}
        />
        <path
          d="M259.677 90.8467H405.954C415.151 90.8467 421.504 100.05 418.242 108.649L355.165 274.942L354.334 277.133L356.631 276.666L442.532 259.188C452.693 257.121 463.006 261.935 467.866 270.995C472.725 280.052 470.98 291.201 463.575 298.377L204.882 549.094C197.035 556.698 184.322 548.393 188.133 538.152L258.307 349.562L259.119 347.379L256.837 347.846L180.817 363.398C172.535 365.093 163.98 362.202 158.47 355.854H158.469C152.96 349.511 151.361 340.707 154.287 332.862V332.861L239.249 105.031C242.43 96.5022 250.574 90.8468 259.677 90.8467ZM274.139 142.961C267.799 140.696 260.647 143.303 254.323 148.771C247.963 154.27 242.185 162.851 238.42 173.191C234.655 183.532 233.573 193.793 234.927 202.053C236.273 210.266 240.102 216.802 246.446 219.069C252.786 221.335 259.937 218.727 266.261 213.26C272.621 207.76 278.399 199.179 282.164 188.839C285.929 178.499 287.012 168.237 285.658 159.978C284.312 151.765 280.483 145.228 274.139 142.961ZM342.532 142.961C336.192 140.696 329.041 143.303 322.717 148.771C316.356 154.27 310.578 162.851 306.813 173.191C303.049 183.532 301.966 193.793 303.319 202.053C304.665 210.266 308.494 216.802 314.839 219.069C321.179 221.335 328.33 218.727 334.654 213.26C341.015 207.76 346.793 199.179 350.558 188.839C354.322 178.499 355.405 168.237 354.052 159.978C352.706 151.765 348.877 145.228 342.532 142.961Z"
          stroke={`url(#${idPrefix}-icon-stroke)`}
          stroke-width="2.58333"
        />
      </g>
    </svg>
  );
};

export const PaperclipIcon = ({
  size = 24,
  className,
}: IconProps): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    class={className}
    fill="currentColor"
  >
    <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5a2.5 2.5 0 0 1 5 0v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5a2.5 2.5 0 0 0 5 0V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" />
  </svg>
);

export const SendIcon = ({ size = 24, className }: IconProps): JSX.Element => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 468 468"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    class={className}
  >
    <g filter="url(#filter0_d_655_33)">
      <rect
        x="36.2969"
        y="24.1973"
        width="395.228"
        height="395.228"
        rx="197.614"
        fill="#444343"
        shape-rendering="crispEdges"
      />
      <path
        d="M221.558 320.618V170.246L152.393 239.411L135.102 221.811L233.908 123.004L332.715 221.811L315.424 239.411L246.259 170.246V320.618H221.558Z"
        fill="white"
      />
    </g>
    <defs>
      <filter
        id="filter0_d_655_33"
        x="0.00165558"
        y="0.000453949"
        width="467.817"
        height="467.818"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="12.0984" />
        <feGaussianBlur stdDeviation="18.1476" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_655_33"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_655_33"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);

/**
 * Drag Handle Icon - 6 dots pattern
 */
export const DragHandleIcon = ({
  size = 24,
  className,
}: IconProps): JSX.Element => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 430 430"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    class={className}
  >
    <path
      d="M161.186 358.194C151.336 358.194 142.904 354.687 135.889 347.672C128.874 340.658 125.367 332.226 125.367 322.375C125.367 312.525 128.874 304.093 135.889 297.078C142.904 290.064 151.336 286.556 161.186 286.556C171.036 286.556 179.469 290.064 186.483 297.078C193.498 304.093 197.005 312.525 197.005 322.375C197.005 332.226 193.498 340.658 186.483 347.672C179.469 354.687 171.036 358.194 161.186 358.194ZM268.643 358.194C258.793 358.194 250.36 354.687 243.346 347.672C236.331 340.658 232.824 332.226 232.824 322.375C232.824 312.525 236.331 304.093 243.346 297.078C250.36 290.064 258.793 286.556 268.643 286.556C278.493 286.556 286.926 290.064 293.94 297.078C300.955 304.093 304.462 312.525 304.462 322.375C304.462 332.226 300.955 340.658 293.94 347.672C286.926 354.687 278.493 358.194 268.643 358.194ZM161.186 250.737C151.336 250.737 142.904 247.23 135.889 240.216C128.874 233.201 125.367 224.769 125.367 214.918C125.367 205.068 128.874 196.636 135.889 189.621C142.904 182.607 151.336 179.099 161.186 179.099C171.036 179.099 179.469 182.607 186.483 189.621C193.498 196.636 197.005 205.068 197.005 214.918C197.005 224.769 193.498 233.201 186.483 240.216C179.469 247.23 171.036 250.737 161.186 250.737ZM268.643 250.737C258.793 250.737 250.36 247.23 243.346 240.216C236.331 233.201 232.824 224.769 232.824 214.918C232.824 205.068 236.331 196.636 243.346 189.621C250.36 182.607 258.793 179.099 268.643 179.099C278.493 179.099 286.926 182.607 293.94 189.621C300.955 196.636 304.462 205.068 304.462 214.918C304.462 224.769 300.955 233.201 293.94 240.216C286.926 247.23 278.493 250.737 268.643 250.737ZM161.186 143.281C151.336 143.281 142.904 139.773 135.889 132.759C128.874 125.744 125.367 117.312 125.367 107.462C125.367 97.6113 128.874 89.1789 135.889 82.1644C142.904 75.1499 151.336 71.6426 161.186 71.6426C171.036 71.6426 179.469 75.1499 186.483 82.1644C193.498 89.1789 197.005 97.6113 197.005 107.462C197.005 117.312 193.498 125.744 186.483 132.759C179.469 139.773 171.036 143.281 161.186 143.281ZM268.643 143.281C258.793 143.281 250.36 139.773 243.346 132.759C236.331 125.744 232.824 117.312 232.824 107.462C232.824 97.6113 236.331 89.1789 243.346 82.1644C250.36 75.1499 258.793 71.6426 268.643 71.6426C278.493 71.6426 286.926 75.1499 293.94 82.1644C300.955 89.1789 304.462 97.6113 304.462 107.462C304.462 117.312 300.955 125.744 293.94 132.759C286.926 139.773 278.493 143.281 268.643 143.281Z"
      fill="#7C9BB1"
      fill-opacity="0.4"
    />
  </svg>
);

/**
 * Collapse Content Icon - for minimizing full mode
 */
export const CollapseContentIcon = ({
  size = 24,
  className,
}: IconProps): JSX.Element => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 310 310"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    class={className}
  >
    <path
      d="M141.94 167.75V245.175H116.132V193.559H64.5156V167.75H141.94ZM193.556 64.5181V116.134H245.172V141.942H167.748V64.5181H193.556Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Close Icon - X mark for removing items
 */
export const CloseIcon = ({ size = 24, className }: IconProps): JSX.Element => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    class={className}
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);
